/**
 * V52: 좌석배치도(Seat Map) FE 타입 정의
 *
 * 회사별 A/B 스냅샷 기반 좌석배치도 타입
 * - 전직원 보기 + 편집권한자 편집
 *
 * @module shared/types/seatMap
 */

// ============================================
// 기본 타입
// ============================================

/**
 * 회사 코드
 */
export type CompanyCode = 'KR' | 'CN';

/**
 * 스냅샷 종류
 * - live: 운영 (A) - 전직원 조회 가능
 * - draft: 작업중 (B) - 편집자만 조회/수정 가능
 */
export type SeatMapVariant = 'live' | 'draft';

/**
 * 오브젝트 타입
 * V52.2: zone 타입 추가 (구역)
 * V55.22: textBox, floor 타입 추가
 */
export type SeatMapObjectType =
  | 'floor'        // V55.22: 바닥 (최하위 레이어)
  | 'zone'         // V52.2: 구역 (배경 영역, 좌석 그룹핑용)
  | 'seat'         // 좌석 (직원 지정 가능)
  | 'printer'      // 프린터
  | 'meeting_room' // 회의실
  | 'kitchen'      // 휴게실
  | 'storage'      // 비품실
  | 'desk'         // 책상
  | 'partition'    // 파티션
  | 'textBox';     // V55.22: 텍스트 박스

/**
 * V55.22: 텍스트 정렬 타입
 */
export type TextAlignX = 'left' | 'center' | 'right';
export type TextAlignY = 'top' | 'middle' | 'bottom';

/**
 * V55.22: 텍스트 색상 팔레트
 */
export const TEXT_COLOR_PALETTE = [
  { value: '#111827', label: 'Black' },
  { value: '#6b7280', label: 'Gray' },
  { value: '#dc2626', label: 'Red' },
  { value: '#ea580c', label: 'Orange' },
  { value: '#ca8a04', label: 'Yellow' },
  { value: '#16a34a', label: 'Green' },
  { value: '#0d9488', label: 'Teal' },
  { value: '#2563eb', label: 'Blue' },
  { value: '#1e3a8a', label: 'Navy' },
  { value: '#7c3aed', label: 'Purple' },
] as const;

// ============================================
// 오브젝트 설정
// ============================================

/**
 * 오브젝트 타입별 설정
 */
export interface SeatMapObjectConfig {
  type: SeatMapObjectType;
  label: string;
  icon: string;  // lucide-react 아이콘명
  defaultWidth: number;
  defaultHeight: number;
  resizable: boolean;
  canAssignEmployee: boolean;
}

/**
 * 오브젝트 타입별 기본 설정
 * V52.2: zone 추가
 * V55.22: floor, textBox 추가
 */
export const SEAT_MAP_OBJECT_CONFIGS: Record<SeatMapObjectType, SeatMapObjectConfig> = {
  // V55.22: 바닥 - 최하위 레이어
  floor: {
    type: 'floor',
    label: '바닥',
    icon: 'Layers',
    defaultWidth: 500,
    defaultHeight: 300,
    resizable: true,
    canAssignEmployee: false,
  },
  zone: {
    type: 'zone',
    label: '구역',
    icon: 'LayoutGrid',
    defaultWidth: 400,
    defaultHeight: 250,
    resizable: true,
    canAssignEmployee: false,
  },
  seat: {
    type: 'seat',
    label: '좌석',
    icon: 'User',
    defaultWidth: 60,
    defaultHeight: 60,
    resizable: false,
    canAssignEmployee: true,
  },
  printer: {
    type: 'printer',
    label: '프린터',
    icon: 'Printer',
    defaultWidth: 80,
    defaultHeight: 60,
    resizable: true,
    canAssignEmployee: false,
  },
  meeting_room: {
    type: 'meeting_room',
    label: '회의실',
    icon: 'Users',
    defaultWidth: 200,
    defaultHeight: 150,
    resizable: true,
    canAssignEmployee: false,
  },
  kitchen: {
    type: 'kitchen',
    label: '휴게실',
    icon: 'Coffee',
    defaultWidth: 150,
    defaultHeight: 100,
    resizable: true,
    canAssignEmployee: false,
  },
  storage: {
    type: 'storage',
    label: '비품실',
    icon: 'Package',
    defaultWidth: 100,
    defaultHeight: 80,
    resizable: true,
    canAssignEmployee: false,
  },
  desk: {
    type: 'desk',
    label: '책상',
    icon: 'Square',
    defaultWidth: 120,
    defaultHeight: 60,
    resizable: true,
    canAssignEmployee: false,
  },
  partition: {
    type: 'partition',
    label: '파티션',
    icon: 'Minus',
    defaultWidth: 200,
    defaultHeight: 10,
    resizable: true,
    canAssignEmployee: false,
  },
  // V55.22: 텍스트 박스
  textBox: {
    type: 'textBox',
    label: '텍스트 박스',
    icon: 'Type',
    defaultWidth: 240,
    defaultHeight: 120,
    resizable: true,
    canAssignEmployee: false,
  },
};

/**
 * 캔버스 기본값
 */
export const SEAT_MAP_DEFAULTS = {
  canvasWidth: 1920,
  canvasHeight: 1080,
  gridSize: 10,
};

// ============================================
// 문서/오브젝트 타입
// ============================================

/**
 * 좌석배치도 오브젝트
 */
export interface SeatMapObject {
  /** 오브젝트 ID (UUID) */
  id: string;

  /** 오브젝트 타입 */
  type: SeatMapObjectType;

  /** X 좌표 */
  x: number;

  /** Y 좌표 */
  y: number;

  /** 너비 */
  width: number;

  /** 높이 */
  height: number;

  /** 회전 각도 (도, 선택) */
  rotation?: number;

  /** 라벨 (회의실명, 프린터명 등) */
  label?: string;

  /** 배정된 직원 ID (type=seat일 때만) */
  employeeId?: string;

  /** 배정된 직원 이름 (캐시) */
  employeeName?: string;

  /** 배정된 직원 직급 (캐시) */
  employeePosition?: string;

  /** Z-Index (레이어 순서) */
  zIndex?: number;

  // ====== V52.2: Zone + 좌석 코드 관련 필드 ======

  /** V52.2: 구역 코드 (type='zone'일 때 사용, 예: "A", "B") */
  zoneCode?: string;

  /** V52.2: 좌석이 속한 zone의 ID (type='seat'일 때 사용) */
  zoneId?: string;

  /** V52.2: 좌석 번호 (type='seat'일 때 사용, 수동 입력, 예: "1", "12") */
  seatNo?: string;

  // ====== V55.22: textBox, floor 관련 필드 ======

  /** V55.22: 텍스트 내용 (textBox, floor에서 사용) */
  text?: string;

  /** V55.22: 글자 크기 (textBox, floor에서 사용, 기본: 16) */
  fontSize?: number;

  /** V55.22: 텍스트 색상 (textBox, floor에서 사용, 기본: #111827) */
  textColor?: string;

  /** V55.22: 가로 정렬 (floor에서 사용, 기본: center) */
  alignX?: TextAlignX;

  /** V55.22: 세로 정렬 (floor에서 사용, 기본: middle) */
  alignY?: TextAlignY;
}

/**
 * 좌석배치도 문서
 */
export interface SeatMapDocument {
  /** 문서 ID (companyCode_variant) */
  id: string;

  /** 회사 코드 */
  companyCode: CompanyCode;

  /** 스냅샷 종류 */
  variant: SeatMapVariant;

  /** 오브젝트 배열 */
  objects: SeatMapObject[];

  /** 캔버스 너비 */
  canvasWidth: number;

  /** 캔버스 높이 */
  canvasHeight: number;

  /** 그리드 스냅 크기 */
  gridSize: number;

  /** 버전 (승격 시 증가) */
  version: number;

  /** 생성일시 */
  createdAt: Date;

  /** 생성자 ID */
  createdBy: string;

  /** 수정일시 */
  updatedAt: Date;

  /** 수정자 ID */
  updatedBy: string;
}

// ============================================
// API 요청/응답 타입
// ============================================

/**
 * getSeatMap 요청
 */
export interface GetSeatMapRequest {
  /** 회사 코드 */
  companyCode: CompanyCode;

  /** 스냅샷 종류 (기본: live) */
  variant?: SeatMapVariant;
}

/**
 * getSeatMap 응답
 */
export interface GetSeatMapResponse {
  success: boolean;

  /** 좌석배치도 문서 (없으면 null) */
  seatMap: SeatMapDocument | null;

  error?: string;
}

/**
 * saveSeatMapDraft 요청
 */
export interface SaveSeatMapDraftRequest {
  /** 회사 코드 */
  companyCode: CompanyCode;

  /** 저장할 데이터 */
  data: {
    /** 오브젝트 배열 */
    objects: SeatMapObject[];

    /** 캔버스 너비 (선택) */
    canvasWidth?: number;

    /** 캔버스 높이 (선택) */
    canvasHeight?: number;

    /** 그리드 크기 (선택) */
    gridSize?: number;
  };
}

/**
 * saveSeatMapDraft 응답
 */
export interface SaveSeatMapDraftResponse {
  success: boolean;
  error?: string;
}

/**
 * promoteSeatMapDraft 요청
 */
export interface PromoteSeatMapDraftRequest {
  /** 회사 코드 */
  companyCode: CompanyCode;
}

/**
 * promoteSeatMapDraft 응답
 */
export interface PromoteSeatMapDraftResponse {
  success: boolean;

  /** 새 버전 번호 */
  version: number;

  error?: string;
}

/**
 * V52.13: copySeatMap 요청
 * - live → draft 복사 (운영 → 작업중)
 */
export interface CopySeatMapRequest {
  /** 회사 코드 */
  companyCode: CompanyCode;

  /** 복사 원본 variant */
  from: SeatMapVariant;

  /** 복사 대상 variant */
  to: SeatMapVariant;
}

/**
 * V52.13: copySeatMap 응답
 */
export interface CopySeatMapResponse {
  success: boolean;
  error?: string;
}

// ============================================
// 편집 권한 roleId
// ============================================

/**
 * 좌석배치도 편집 가능 역할 ID
 *
 * 근거: functions/src/modules/auth/roles.ts:529-575
 */
export const SEAT_MAP_EDITOR_ROLES = [
  'system_admin',
  'role_admin_kr',
  'role_admin_cn',
  'role_hr',
  'role_manager',
  'role_exec',
] as const;

export type SeatMapEditorRole = (typeof SEAT_MAP_EDITOR_ROLES)[number];

// ============================================
// 유틸리티 함수
// ============================================

/**
 * 새 오브젝트 ID 생성 (UUID v4)
 */
export function generateObjectId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 오브젝트 타입의 기본 설정 조회
 */
export function getObjectConfig(type: SeatMapObjectType): SeatMapObjectConfig {
  return SEAT_MAP_OBJECT_CONFIGS[type];
}

/**
 * 새 오브젝트 생성 (기본값 적용)
 * V52.6: width/height 유효성 검사 추가 - options에서 0/NaN이 들어오면 기본값 사용
 */
export function createNewObject(
  type: SeatMapObjectType,
  x: number,
  y: number,
  options?: Partial<SeatMapObject>
): SeatMapObject {
  const config = getObjectConfig(type);

  // 기본 오브젝트 생성
  const obj: SeatMapObject = {
    id: generateObjectId(),
    type,
    x,
    y,
    width: config.defaultWidth,
    height: config.defaultHeight,
    ...options,
  };

  // V52.6: width/height 유효성 검사 (options에서 잘못된 값이 들어오면 기본값으로 복원)
  if (!obj.width || !Number.isFinite(obj.width) || obj.width <= 0) {
    obj.width = config.defaultWidth;
  }
  if (!obj.height || !Number.isFinite(obj.height) || obj.height <= 0) {
    obj.height = config.defaultHeight;
  }

  return obj;
}

/**
 * 좌표를 그리드에 스냅
 */
export function snapToGrid(value: number, gridSize: number): number {
  return Math.round(value / gridSize) * gridSize;
}

/**
 * 오브젝트가 캔버스 범위 내인지 확인
 */
export function isWithinCanvas(
  obj: SeatMapObject,
  canvasWidth: number,
  canvasHeight: number
): boolean {
  return (
    obj.x >= 0 &&
    obj.y >= 0 &&
    obj.x + obj.width <= canvasWidth &&
    obj.y + obj.height <= canvasHeight
  );
}

/**
 * 오브젝트 위치를 캔버스 범위 내로 제한
 */
export function clampToCanvas(
  obj: SeatMapObject,
  canvasWidth: number,
  canvasHeight: number
): SeatMapObject {
  return {
    ...obj,
    x: Math.max(0, Math.min(obj.x, canvasWidth - obj.width)),
    y: Math.max(0, Math.min(obj.y, canvasHeight - obj.height)),
  };
}

// ============================================
// V52.2: Zone + 좌석 코드 유틸리티
// ============================================

/**
 * V52.2: 좌석 표시 코드 생성
 *
 * zoneCode + seatNo → "A-1" 형식
 * 둘 중 하나라도 없으면 있는 것만 표시
 *
 * @param zoneCode 구역 코드 (예: "A")
 * @param seatNo 좌석 번호 (예: "1")
 * @returns 표시 코드 (예: "A-1", "A", "1", "")
 */
export function formatSeatCode(zoneCode?: string, seatNo?: string): string {
  if (zoneCode && seatNo) {
    return `${zoneCode}-${seatNo}`;
  }
  if (zoneCode) return zoneCode;
  if (seatNo) return seatNo;
  return '';
}

/**
 * V52.2: 좌석에 표시할 라벨 생성
 *
 * 좌석 코드 + 직원명 조합
 *
 * @param obj 좌석 오브젝트
 * @param zones zone 오브젝트 배열 (zoneCode 조회용)
 * @returns 표시할 라벨
 */
export function getSeatDisplayLabel(
  obj: SeatMapObject,
  zones: SeatMapObject[]
): { seatCode: string; employeeName?: string } {
  // zoneId로 zone 찾아서 zoneCode 가져오기
  const zone = obj.zoneId ? zones.find((z) => z.id === obj.zoneId) : undefined;
  const zoneCode = zone?.zoneCode;

  const seatCode = formatSeatCode(zoneCode, obj.seatNo);

  return {
    seatCode,
    employeeName: obj.employeeName,
  };
}

// ============================================
// V54: 다중 템플릿 시스템 타입
// ============================================

/**
 * V54: 좌석배치도 템플릿
 */
export interface SeatMapTemplate {
  /** 템플릿 ID (UUID) */
  templateId: string;
  /** 템플릿 이름 (필수) */
  name: string;
  /** 오브젝트 배열 */
  objects: SeatMapObject[];
  /** 캔버스 너비 */
  canvasWidth: number;
  /** 캔버스 높이 */
  canvasHeight: number;
  /** 그리드 크기 */
  gridSize: number;
  /** 생성일시 */
  createdAt: Date;
  /** 생성자 */
  createdBy: string;
  /** 수정일시 */
  updatedAt: Date;
  /** 수정자 */
  updatedBy: string;
}

/**
 * V54: 좌석배치도 설정
 */
export interface SeatMapSettings {
  /** 운영 템플릿 ID */
  activeTemplateId: string | null;
  /** 수정일시 */
  updatedAt: Date;
  /** 수정자 */
  updatedBy: string;
}

/**
 * V54: 템플릿 목록 아이템 (UI 표시용)
 */
export interface SeatMapTemplateListItem {
  templateId: string;
  name: string;
  objectCount: number;
  updatedAt: Date;
  isActive: boolean;
}

// ============================================
// V54: API 요청/응답 타입
// ============================================

/** getActiveSeatMap 요청 */
export interface GetActiveSeatMapRequest {
  companyCode: CompanyCode;
}

/** getActiveSeatMap 응답 */
export interface GetActiveSeatMapResponse {
  success: boolean;
  template: SeatMapTemplate | null;
  activeTemplateId: string | null;
  error?: string;
}

/** listSeatMapTemplates 요청 */
export interface ListSeatMapTemplatesRequest {
  companyCode: CompanyCode;
}

/** listSeatMapTemplates 응답 */
export interface ListSeatMapTemplatesResponse {
  success: boolean;
  templates: SeatMapTemplateListItem[];
  activeTemplateId: string | null;
  error?: string;
}

/** getSeatMapTemplate 요청 */
export interface GetSeatMapTemplateRequest {
  companyCode: CompanyCode;
  templateId: string;
}

/** getSeatMapTemplate 응답 */
export interface GetSeatMapTemplateResponse {
  success: boolean;
  template: SeatMapTemplate | null;
  error?: string;
}

/** createSeatMapTemplate 요청 */
export interface CreateSeatMapTemplateRequest {
  companyCode: CompanyCode;
  name?: string;
  copyFromTemplateId?: string;
}

/** createSeatMapTemplate 응답 */
export interface CreateSeatMapTemplateResponse {
  success: boolean;
  templateId: string;
  name: string;
  error?: string;
}

/** saveSeatMapTemplate 요청 */
export interface SaveSeatMapTemplateRequest {
  companyCode: CompanyCode;
  templateId: string;
  name: string;
  objects: SeatMapObject[];
  canvasWidth?: number;
  canvasHeight?: number;
  gridSize?: number;
}

/** saveSeatMapTemplate 응답 */
export interface SaveSeatMapTemplateResponse {
  success: boolean;
  error?: string;
}

/** copySeatMapTemplate 요청 */
export interface CopySeatMapTemplateRequest {
  companyCode: CompanyCode;
  fromTemplateId: string;
  toTemplateId: string;
}

/** copySeatMapTemplate 응답 */
export interface CopySeatMapTemplateResponse {
  success: boolean;
  error?: string;
}

/** setActiveSeatMapTemplate 요청 */
export interface SetActiveSeatMapTemplateRequest {
  companyCode: CompanyCode;
  templateId: string;
}

/** setActiveSeatMapTemplate 응답 */
export interface SetActiveSeatMapTemplateResponse {
  success: boolean;
  error?: string;
}

/** deleteSeatMapTemplate 요청 */
export interface DeleteSeatMapTemplateRequest {
  companyCode: CompanyCode;
  templateId: string;
}

/** deleteSeatMapTemplate 응답 */
export interface DeleteSeatMapTemplateResponse {
  success: boolean;
  error?: string;
}
